<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, markRaw, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import MainTitleComponent from '@/components/ui/MainTitleComponent.vue';
import DividerComponent from '@/components/ui/DividerComponent.vue';
import ResolveDuplicateReviewComponent from '@/components/identityDuplicateReview/ResolveDuplicateReviewComponent.vue';
import IdentityDuplicateReviewService from '@/services/identityDuplicateReview/identityDuplicateReviewService.ts';
import { useAuthStore } from '@/stores/authStore.ts';
import { displayModal } from '@/utils/modalUtils.ts';
import { displayToast } from '@/utils/toastUtils.ts';
import { IdentityDuplicateReviewStatusEnum } from '@/enums/identityDuplicateReview_enum.ts';
import { formatFullDate, formatIdentityDuplicateReviewStatus, formatItemValue } from '@/utils/formatters.ts';

import type {
  DuplicateReviewMatch,
  DuplicateReviewNoteRequest,
  IdentityDuplicateReview
} from '@/types/services/identityDuplicateReview/identityDuplicateReview.type.ts';

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const reviewId = Number(route.params.id)
const review = ref<IdentityDuplicateReview | null>(null)
const loading = ref<boolean>(false)

const canAct = computed<boolean>(() =>
  authStore.hasPermission(['UPDATE_IDENTITY_DUPLICATE_REVIEW']) &&
  review.value?.status === IdentityDuplicateReviewStatusEnum.OPEN
)

function fullName(user: { firstName: string, lastName: string, email: string }): string {
  return `${user.firstName} ${user.lastName}`.trim() || user.email
}

function birthPlaceKey(match: DuplicateReviewMatch): 'same' | 'different' | 'unknown' {
  if (match.birthPlaceMatch === true) return 'same'
  if (match.birthPlaceMatch === false) return 'different'
  return 'unknown'
}

function birthPlaceColor(match: DuplicateReviewMatch): string {
  const key = birthPlaceKey(match)
  return key === 'same' ? 'error' : (key === 'different' ? 'success' : 'warning')
}

async function fetchReview(): Promise<void> {
  try {
    loading.value = true
    review.value = await IdentityDuplicateReviewService.get(reviewId)
  } catch {
    // Error toast already shown (404 included).
    await router.push('/identity-duplicate-reviews')
  } finally {
    loading.value = false
  }
}

onMounted(fetchReview)

// A 409 means someone else already resolved the review: reload to show its real state.
async function runAction(action: () => Promise<IdentityDuplicateReview>, successMessage: string): Promise<void> {
  try {
    loading.value = true
    review.value = await action()
    displayToast(successMessage, 'success')
  } catch {
    try {
      review.value = await IdentityDuplicateReviewService.get(reviewId)
    } catch {
      // ignore: the original error was already displayed
    }
  } finally {
    loading.value = false
  }
}

async function resolve(mode: 'clear' | 'confirm'): Promise<void> {
  if (!review.value) return

  const modal = await displayModal({
    component: markRaw(ResolveDuplicateReviewComponent),
    props: { mode, userName: fullName(review.value.user) }
  })

  if (modal && typeof modal === 'object') {
    const { note } = modal as DuplicateReviewNoteRequest
    if (mode === 'clear') {
      await runAction(() => IdentityDuplicateReviewService.clear(reviewId, note), t('views.identityDuplicateReviewDetail.clearSuccess'))
    } else {
      await runAction(() => IdentityDuplicateReviewService.confirm(reviewId, note), t('views.identityDuplicateReviewDetail.confirmSuccess'))
    }
  }
}
</script>

<template>
  <MainTitleComponent icon="PhUsersFour">
    {{ t('views.identityDuplicateReviewDetail.title', { id: reviewId }) }}

    <template v-if="review && canAct" #actions>
      <v-col cols="12" sm="auto" class="d-flex flex-column flex-sm-row ga-3">
        <v-btn :disabled="loading" variant="outlined" @click.prevent="resolve('clear')">
          {{ t('views.identityDuplicateReviewDetail.buttons.clear') }}
          <template #prepend><v-icon icon="PhCheck" /></template>
        </v-btn>
        <v-btn :disabled="loading" color="error" @click.prevent="resolve('confirm')">
          {{ t('views.identityDuplicateReviewDetail.buttons.confirm') }}
          <template #prepend><v-icon icon="PhCopy" /></template>
        </v-btn>
      </v-col>
    </template>
  </MainTitleComponent>

  <v-skeleton-loader v-if="loading && !review" type="article" />

  <template v-if="review">
    <v-row align="center" class="details">
      <v-col cols="12" sm="6" md="4">
        <p class="text-label">{{ t('views.identityDuplicateReviewDetail.status') }}</p>
        <v-chip :color="formatIdentityDuplicateReviewStatus(review.status)" size="small">
          {{ t(`views.identityDuplicateReviewList.status.${review.status}`) }}
        </v-chip>
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <p class="text-label">{{ t('views.identityDuplicateReviewDetail.createdAt') }}</p>
        <p class="text-value">{{ formatFullDate(review.createdAt) }}</p>
      </v-col>
    </v-row>

    <DividerComponent />

    <v-row class="details">
      <v-col cols="12">
        <p class="text-label font-weight-bold">{{ t('views.identityDuplicateReviewDetail.client') }}</p>
        <p class="text-value">{{ fullName(review.user) }}</p>
        <p class="text-label">{{ review.user.email }} (#{{ review.user.id }})</p>
        <v-btn
          :to="`/users/${review.user.id}/detail`"
          class="mt-2"
          size="small"
          variant="text"
        >
          {{ t('views.identityDuplicateReviewDetail.viewClient') }}
        </v-btn>
      </v-col>
    </v-row>

    <DividerComponent />

    <v-row>
      <v-col cols="12">
        <p class="text-label font-weight-bold mb-2">{{ t('views.identityDuplicateReviewDetail.matches.title') }}</p>
        <p v-if="review.matches.length === 0" class="text-value">
          {{ t('views.identityDuplicateReviewDetail.matches.empty') }}
        </p>
      </v-col>
      <v-col v-for="match in review.matches" :key="match.userId" cols="12" md="6">
        <v-card class="pa-3" variant="outlined">
          <p class="text-value">{{ fullName(match) }}</p>
          <p class="text-label">{{ match.email }} (#{{ match.userId }})</p>
          <v-chip :color="birthPlaceColor(match)" class="mt-2" size="small">
            {{ t(`views.identityDuplicateReviewDetail.matches.birthPlace.${birthPlaceKey(match)}`) }}
          </v-chip>
          <div>
            <v-btn
              :to="`/users/${match.userId}/detail`"
              class="mt-2"
              size="small"
              variant="text"
            >
              {{ t('views.identityDuplicateReviewDetail.matches.viewAccount') }}
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <template v-if="review.resolvedAt">
      <DividerComponent />
      <v-row class="details">
        <v-col cols="12" sm="6" md="4">
          <p class="text-label">{{ t('views.identityDuplicateReviewDetail.resolvedAt') }}</p>
          <p class="text-value">{{ formatFullDate(review.resolvedAt) }}</p>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <p class="text-label">{{ t('views.identityDuplicateReviewDetail.resolvedBy') }}</p>
          <p class="text-value">#{{ review.resolvedById }}</p>
        </v-col>
        <v-col cols="12">
          <p class="text-label">{{ t('views.identityDuplicateReviewDetail.resolutionNote') }}</p>
          <p class="text-value">{{ formatItemValue(review.resolutionNote) }}</p>
        </v-col>
      </v-row>
    </template>
  </template>
</template>
